/**
 * Procedural Lviv rooftop silhouette for the maintenance page.
 *
 * Emitted as an SVG data URI so it can be used as a *repeating CSS background*.
 * That is the whole trick: `background-repeat: repeat-x` plus an animation on
 * `background-position-x` from 0 to -1200px gives a seamless, infinite parallax
 * drift with no JavaScript running per frame and no library.
 *
 * The skyline is generated, not hand-drawn, but it is NOT random at runtime:
 * a seeded PRNG means the same seed always produces the same street, so the
 * layers do not reshuffle on every React re-render.
 *
 * Shapes are the ones that make a Lviv street read as Lviv: steep gables,
 * mansards, a couple of tower spires and a baroque onion dome.
 *
 * ES5 only — this project is Create React App 2.1.3 (no `?.`, no `??`).
 */

export var TILE_WIDTH = 1200;

/* -------------------------------------------------------------------------- */
/* Deterministic PRNG (mulberry32)                                            */
/* -------------------------------------------------------------------------- */

function mulberry32(seed) {
  var t = seed >>> 0;
  return function () {
    t = (t + 0x6d2b79f5) >>> 0;
    var r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function round(value) {
  return Math.round(value * 100) / 100;
}

/* -------------------------------------------------------------------------- */
/* Roof shapes — each returns the path segment for one building               */
/* -------------------------------------------------------------------------- */

/** Steep gable, with the small eaves overhang that reads as "old town". */
function gable(x, w, wallTop, roofTop) {
  return (
    " L" + round(x) + "," + round(wallTop) +
    " L" + round(x - 4) + "," + round(wallTop) +
    " L" + round(x + w / 2) + "," + round(roofTop) +
    " L" + round(x + w + 4) + "," + round(wallTop) +
    " L" + round(x + w) + "," + round(wallTop)
  );
}

/** Trapezoid mansard — flatter, wider, very common on Lviv facades. */
function mansard(x, w, wallTop, roofTop) {
  return (
    " L" + round(x) + "," + round(wallTop) +
    " L" + round(x + w * 0.18) + "," + round(roofTop) +
    " L" + round(x + w * 0.82) + "," + round(roofTop) +
    " L" + round(x + w) + "," + round(wallTop)
  );
}

/** Flat roof with a chimney stub, to break up the rhythm. */
function flat(x, w, wallTop) {
  var cx = x + w * 0.68;
  var ch = 12;
  return (
    " L" + round(x) + "," + round(wallTop) +
    " L" + round(cx) + "," + round(wallTop) +
    " L" + round(cx) + "," + round(wallTop - ch) +
    " L" + round(cx + 9) + "," + round(wallTop - ch) +
    " L" + round(cx + 9) + "," + round(wallTop) +
    " L" + round(x + w) + "," + round(wallTop)
  );
}

/** Tower spire with a finial ball — the cathedral/town hall note. */
function spire(x, w, wallTop, roofTop) {
  var cx = x + w / 2;
  var ballY = roofTop - 7;
  return (
    " L" + round(x) + "," + round(wallTop) +
    " L" + round(x + w * 0.12) + "," + round(wallTop - 10) +
    " L" + round(cx) + "," + round(roofTop) +
    " L" + round(x + w * 0.88) + "," + round(wallTop - 10) +
    " L" + round(x + w) + "," + round(wallTop) +
    // finial: a small circle drawn with two arcs, so it stays in one path
    " M" + round(cx - 4) + "," + round(ballY) +
    " a4,4 0 1,0 8,0 a4,4 0 1,0 -8,0" +
    " M" + round(x + w) + "," + round(wallTop)
  );
}

/** Baroque onion dome. */
function dome(x, w, wallTop, roofTop) {
  var height = wallTop - roofTop;
  return (
    " L" + round(x) + "," + round(wallTop) +
    " C" + round(x - w * 0.06) + "," + round(wallTop - height * 0.55) +
    " " + round(x + w * 0.2) + "," + round(wallTop - height) +
    " " + round(x + w / 2) + "," + round(roofTop) +
    " C" + round(x + w * 0.8) + "," + round(wallTop - height) +
    " " + round(x + w * 1.06) + "," + round(wallTop - height * 0.55) +
    " " + round(x + w) + "," + round(wallTop)
  );
}

/* -------------------------------------------------------------------------- */
/* Tile                                                                        */
/* -------------------------------------------------------------------------- */

function pickKind(r) {
  if (r < 0.06) return "spire";
  if (r < 0.11) return "dome";
  if (r < 0.42) return "mansard";
  if (r < 0.56) return "flat";
  return "gable";
}

/**
 * Build one repeating tile.
 *
 * The seam is invisible because the run of buildings always stops short of the
 * right edge, so the tile begins and ends on the ground line — the join looks
 * like any other gap between two houses.
 */
function buildTile(options) {
  var rand = mulberry32(options.seed);
  var H = options.height;
  var minScale = options.minScale;
  var maxScale = options.maxScale;

  var body = "M0," + H;
  var windows = "";
  var x = 8;

  // Stop close to the right edge: the leftover ground is what the seam looks
  // like, so a big trailing gap would show up as a repeating hole in the street.
  while (x < TILE_WIDTH - 60) {
    var kind = pickKind(rand());
    var w;
    var wallTop;
    var roofTop;

    if (kind === "spire") {
      w = Math.round(26 + rand() * 14);
      wallTop = H - H * (minScale + rand() * (maxScale - minScale)) * 1.2;
      roofTop = wallTop - (70 + rand() * 60);
    } else if (kind === "dome") {
      w = Math.round(52 + rand() * 26);
      wallTop = H - H * (minScale + rand() * (maxScale - minScale)) * 0.9;
      roofTop = wallTop - (34 + rand() * 26);
    } else {
      w = Math.round(54 + rand() * 96);
      // Squared, so short houses are common and tall ones are the exception —
      // a uniform distribution reads as a downtown skyline, not an old town.
      var t = rand();
      wallTop = H - H * (minScale + t * t * (maxScale - minScale));
      roofTop = wallTop - w * (0.30 + rand() * 0.26);
    }

    if (roofTop < 6) roofTop = 6;
    if (x + w > TILE_WIDTH - 12) break;

    if (kind === "gable") body += gable(x, w, wallTop, roofTop);
    else if (kind === "mansard") body += mansard(x, w, wallTop, roofTop);
    else if (kind === "flat") body += flat(x, w, wallTop);
    else if (kind === "spire") body += spire(x, w, wallTop, roofTop);
    else body += dome(x, w, wallTop, roofTop);

    body += " L" + round(x + w) + "," + H;

    // Lit windows, front layer only — a silhouette alone reads as abandoned.
    if (options.windows && kind !== "spire" && kind !== "dome") {
      var cols = Math.floor((w - 14) / 17);
      var rows = Math.floor((H - wallTop - 16) / 22);
      var c;
      var rw;
      for (c = 0; c < cols; c++) {
        for (rw = 0; rw < rows; rw++) {
          if (rand() > 0.42) continue;
          var wx = x + 10 + c * 17;
          var wy = wallTop + 12 + rw * 22;
          windows +=
            " M" + round(wx) + "," + round(wy) +
            " h6 v9 h-6 Z";
        }
      }
    }

    x += w + Math.round(rand() * 13);
  }

  body += " L" + TILE_WIDTH + "," + H + " Z";
  return { body: body, windows: windows };
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A ready-to-use CSS `background-image` value: `url("data:image/svg+xml,...")`.
 *
 * options: { seed, height, color, minScale, maxScale, windows, windowColor }
 */
export function skylineLayer(options) {
  var tile = buildTile(options);

  var svg =
    "<svg xmlns='http://www.w3.org/2000/svg' width='" + TILE_WIDTH +
    "' height='" + options.height +
    "' viewBox='0 0 " + TILE_WIDTH + " " + options.height + "'>" +
    "<path d='" + tile.body + "' fill='" + options.color + "'/>";

  if (tile.windows) {
    svg +=
      "<path d='" + tile.windows + "' fill='" + options.windowColor +
      "' fill-opacity='0.75'/>";
  }

  svg += "</svg>";

  // encodeURIComponent escapes the '#' in hex colours, which would otherwise
  // terminate the data URI — but it leaves ' ( ) alone, and those three would
  // close the url(...) or the attribute quoting. Escaping them here keeps the
  // returned value safe to drop into a stylesheet, an sx prop or an inline
  // style attribute alike.
  var encoded = encodeURIComponent(svg)
    .replace(/'/g, "%27")
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29");

  return "url('data:image/svg+xml," + encoded + "')";
}
